import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';

import { TableToolbar } from './TableToolbar.js';
import {
    useDataGridStyles,
    formatTime,
    formatCurrency,
    MAX_ROWS,
    dateComparator,
} from './datagridutil';

function renderOwner(params) {
    return params.value ? (
        <Tooltip title={params.value}>
            <span>{params.value}</span>
        </Tooltip>
    ) : (
        <span style={{ color: 'red' }}>No owner!</span>
    );
}

function renderIsActive(params) {
    const expiresAt = new Date(params.value.replace(' ', 'T'));
    const now = new Date();

    const color = expiresAt > now ? 'green' : 'grey';

    return <span style={{ color }}>{formatTime(params)}</span>;
}

function renderStripe(params) {
    if (!params.value) {
        return <span>No</span>;
    }

    return (
        <a
            href={`https://dashboard.stripe.com/customers/${params.value}`}
            target="_blank"
            rel="noopener noreferrer"
        >
            Yes
        </a>
    );
}

function renderTrial(params) {
    if (params.value && params.value === true) {
        return <span style={{ color: '#ed6c02' }}>Yes</span>;
    } else {
        return <span>No</span>;
    }
}

function renderDatadogSSO(params) {
    const { ddDomain, ddOrgId } = params.row;
    return <span>{`${Boolean(ddDomain && ddOrgId)}`}</span>;
}

function getDomainForEmail(email) {
    return email && email.replace(/.*@/, '');
}

function CustomerTable(props) {
    const {
        customers,
        onSelectCustomer,
        single,
        showAll,
        showPayments,
        initialState,
    } = props;
    const classes = useDataGridStyles();

    if (!(customers?.length > 0)) {
        return null;
    }

    const singlePage = single || customers.length <= MAX_ROWS;
    const autoHeight = showAll || singlePage;

    const columns = [
        { field: 'id', headerName: 'ID', flex: 1 },
        { field: 'name', headerName: 'Name', flex: 3 },
        { field: 'planId', headerName: 'Plan', flex: 1 },
        { field: 'quantity', headerName: 'Quantity', width: 70 },
        {
            field: 'stripeId',
            headerName: 'Stripe',
            width: 50,
            renderCell: renderStripe,
        },
        { field: 'awsMarketplaceId', headerName: 'AWS Marketplace', width: 50 },
        {
            field: 'ownerName',
            headerName: 'Owner name',
            flex: 1,
            valueGetter: ({ row }) => row?.Owner.name,
        },
        {
            field: 'ownerEmail',
            headerName: 'Owner email',
            flex: 1,
            renderCell: renderOwner,
            valueGetter: ({ row }) => row?.Owner.email,
        },
        {
            field: 'domain',
            headerName: 'Domain',
            flex: 1,
            valueGetter: (params) => {
                return getDomainForEmail(params.row?.Owner?.email);
            },
        },
        {
            field: 'createdAt',
            headerName: 'Created',
            width: 100,
            valueGetter: formatTime,
            sortComparator: dateComparator,
        },
        {
            field: 'activeUntil',
            headerName: 'Active until',
            width: 140,
            renderCell: renderIsActive,
            valueGetter: formatTime,
            sortComparator: dateComparator,
        },
        {
            field: 'trial',
            headerName: 'Trial',
            flex: 1,
            renderCell: renderTrial,
        },
        {
            field: 'updatedAt',
            headerName: 'Updated',
            flex: 1,
            valueGetter: formatTime,
            sortComparator: dateComparator,
        },
    ];

    if (showPayments) {
        columns.push(
            {
                field: 'mrr',
                headerName: 'MRR',
                flex: 1,
                valueGetter: formatCurrency,
            },
            {
                field: 'arr',
                headerName: 'ARR',
                flex: 1,
                valueGetter: formatCurrency,
            },
            { field: 'cancelAtPeriodEnd', headerName: 'Canceling', flex: 1 },
        );
    }

    columns.push({
        field: 'datadogSSO',
        headerName: 'Datadog SSO',
        flex: 1,
        renderCell: renderDatadogSSO,
    });

    return (
        <Paper
            className={classes.root}
            sx={{ height: autoHeight ? undefined : 129 + MAX_ROWS * 36 }}
        >
            <DataGrid
                slots={{ toolbar: TableToolbar }}
                slotProps={{ toolbar: { csvFileName: 'cc-customers' } }}
                autoHeight={autoHeight}
                rows={customers}
                columns={columns}
                hideFooter={singlePage}
                hideFooterSelectedRowCount
                isRowSelectable={() => false}
                onRowClick={(params) =>
                    onSelectCustomer && onSelectCustomer(params.row.id)
                }
                className={
                    autoHeight ? classes.dataGridAutoHeight : classes.dataGrid
                }
                density="compact"
                initialState={
                    initialState || {
                        columns: {
                            columnVisibilityModel: {
                                id: false,
                                trial: false,
                                domain: false,
                                awsMarketplaceId: false,
                            },
                        },
                    }
                }
                sx={
                    single
                        ? {}
                        : {
                              '& .MuiDataGrid-row:hover': {
                                  cursor: 'pointer',
                              },
                          }
                }
            />
        </Paper>
    );
}

export default CustomerTable;
